'use client';

import { Fragment, useMemo, useState } from 'react';

async function copyText(value: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'absolute';
  textarea.style.left = '-9999px';
  document.body.append(textarea);
  textarea.select();
  document.execCommand('copy');
  textarea.remove();
}

type FlagKey = 'g' | 'i' | 'm' | 's' | 'u' | 'y';

const FLAG_OPTIONS: { key: FlagKey; label: string }[] = [
  { key: 'g', label: 'g · global' },
  { key: 'i', label: 'i · ignore case' },
  { key: 'm', label: 'm · multiline' },
  { key: 's', label: 's · dotAll' },
  { key: 'u', label: 'u · unicode' },
  { key: 'y', label: 'y · sticky' },
];

const COMMON_PATTERNS: { label: string; pattern: string }[] = [
  { label: 'Email', pattern: '[\\w.+-]+@[\\w-]+\\.[\\w.-]+' },
  { label: 'URL', pattern: 'https?://[^\\s"\'<>]+' },
  { label: 'Digits', pattern: '\\d+' },
  { label: 'Word', pattern: '\\b\\w+\\b' },
  { label: 'IPv4', pattern: '\\b(?:\\d{1,3}\\.){3}\\d{1,3}\\b' },
  { label: 'Hex color', pattern: '#(?:[0-9a-fA-F]{3}){1,2}\\b' },
  { label: 'Date (ISO)', pattern: '\\d{4}-\\d{2}-\\d{2}' },
  { label: 'Whitespace', pattern: '\\s+' },
];

type MatchInfo = {
  index: number;
  end: number;
  value: string;
  groups: (string | undefined)[];
  namedGroups: Record<string, string | undefined> | null;
};

type EvalResult = { ok: true; matches: MatchInfo[] } | { ok: false; error: string };

function evaluate(pattern: string, flags: string, testString: string): EvalResult {
  if (!pattern) {
    return { ok: true, matches: [] };
  }

  let regex: RegExp;
  try {
    regex = new RegExp(pattern, flags);
  } catch (error) {
    return {
      ok: false,
      error: error instanceof Error ? error.message : 'Invalid regular expression.',
    };
  }

  const matches: MatchInfo[] = [];
  const isGlobal = flags.includes('g') || flags.includes('y');

  if (!isGlobal) {
    const match = regex.exec(testString);
    if (match) {
      matches.push({
        index: match.index,
        end: match.index + match[0].length,
        value: match[0],
        groups: match.slice(1),
        namedGroups: match.groups ? { ...match.groups } : null,
      });
    }
    return { ok: true, matches };
  }

  let guard = 0;
  let match: RegExpExecArray | null;
  while ((match = regex.exec(testString)) !== null) {
    matches.push({
      index: match.index,
      end: match.index + match[0].length,
      value: match[0],
      groups: match.slice(1),
      namedGroups: match.groups ? { ...match.groups } : null,
    });
    if (match[0] === '') {
      regex.lastIndex += 1;
    }
    guard += 1;
    if (guard > 100_000) {
      break;
    }
  }

  return { ok: true, matches };
}

type Segment = { text: string; matchIndex: number | null };

function buildSegments(testString: string, matches: MatchInfo[]): Segment[] {
  if (matches.length === 0) {
    return [{ text: testString, matchIndex: null }];
  }

  const segments: Segment[] = [];
  let cursor = 0;

  matches.forEach((match, index) => {
    if (match.index < cursor) {
      return;
    }
    if (match.index > cursor) {
      segments.push({ text: testString.slice(cursor, match.index), matchIndex: null });
    }
    segments.push({ text: testString.slice(match.index, match.end), matchIndex: index });
    cursor = match.end;
  });

  if (cursor < testString.length) {
    segments.push({ text: testString.slice(cursor), matchIndex: null });
  }

  return segments;
}

export default function BrowserRegexTester() {
  const [pattern, setPattern] = useState('[\\w.+-]+@[\\w-]+\\.[\\w.-]+');
  const [flags, setFlags] = useState<Record<FlagKey, boolean>>({
    g: true,
    i: false,
    m: false,
    s: false,
    u: false,
    y: false,
  });
  const [testString, setTestString] = useState(
    'Contact us at hello@cleanor.app or support@example.com for help.',
  );
  const [copyMessage, setCopyMessage] = useState<string | null>(null);

  const flagString = useMemo(
    () =>
      FLAG_OPTIONS.filter((option) => flags[option.key])
        .map((option) => option.key)
        .join(''),
    [flags],
  );

  const result = useMemo(
    () => evaluate(pattern, flagString, testString),
    [pattern, flagString, testString],
  );

  const segments = useMemo(
    () => (result.ok ? buildSegments(testString, result.matches) : []),
    [result, testString],
  );

  function toggleFlag(key: FlagKey) {
    setFlags((prev) => ({ ...prev, [key]: !prev[key] }));
  }

  function insertPattern(value: string) {
    setPattern(value);
    setCopyMessage(null);
  }

  async function handleCopyMatches() {
    if (!result.ok) {
      return;
    }
    try {
      const text = result.matches.map((match) => match.value).join('\n');
      await copyText(text);
      setCopyMessage('Matches copied to clipboard.');
    } catch {
      setCopyMessage('The browser could not copy the matches.');
    }
  }

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">Regex</span>
        <h2 className="tool-live-title">Regular expression tester</h2>
        <p className="tool-live-subtitle">
          Build a pattern, pick flags, and see every match highlighted live with capture groups and
          indices. Everything runs in your browser.
        </p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <div className="tool-live-controls">
            <label className="tool-live-field tool-live-field-full">
              <span>Pattern</span>
              <textarea
                onChange={(event) => setPattern(event.target.value)}
                placeholder="Enter a regular expression"
                rows={2}
                spellCheck={false}
                value={pattern}
              />
            </label>

            <div className="tool-live-field tool-live-field-full">
              <span>Flags</span>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem' }}>
                {FLAG_OPTIONS.map((option) => (
                  <label
                    key={option.key}
                    style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}
                  >
                    <input
                      checked={flags[option.key]}
                      onChange={() => toggleFlag(option.key)}
                      type="checkbox"
                    />
                    <span>{option.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="tool-live-field tool-live-field-full">
              <span>Quick patterns</span>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                {COMMON_PATTERNS.map((item) => (
                  <button
                    className="button button-secondary"
                    key={item.label}
                    onClick={() => insertPattern(item.pattern)}
                    type="button"
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </div>

            <label className="tool-live-field tool-live-field-full">
              <span>Test string</span>
              <textarea
                onChange={(event) => setTestString(event.target.value)}
                placeholder="Paste the text to test the pattern against"
                rows={8}
                spellCheck={false}
                value={testString}
              />
            </label>
          </div>

          <div className="tool-live-actions">
            <button
              className="button button-secondary"
              disabled={!result.ok || result.matches.length === 0}
              onClick={handleCopyMatches}
              type="button"
            >
              Copy matches
            </button>
          </div>
          {copyMessage ? <p className="status-message status-success">{copyMessage}</p> : null}
          {!result.ok ? (
            <p className="status-message" style={{ color: '#dc2626' }}>
              {result.error}
            </p>
          ) : null}
        </section>

        <section className="tool-live-preview">
          <div className="tool-live-field tool-live-field-full">
            <span>
              Highlighted matches
              {result.ok ? ` · ${result.matches.length} found` : ''}
            </span>
            <div
              style={{
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                fontFamily: 'var(--font-mono, monospace)',
                fontSize: '0.9rem',
                lineHeight: 1.7,
                padding: '0.85rem',
                borderRadius: '0.6rem',
                border: '1px solid rgba(148, 163, 184, 0.35)',
                minHeight: '6rem',
                maxHeight: '18rem',
                overflow: 'auto',
              }}
            >
              {result.ok && testString.length > 0 ? (
                segments.map((segment, index) =>
                  segment.matchIndex === null ? (
                    <Fragment key={index}>{segment.text}</Fragment>
                  ) : (
                    <mark
                      key={index}
                      style={{
                        backgroundColor: 'rgba(250, 204, 21, 0.45)',
                        borderRadius: '0.2rem',
                        padding: '0.05rem 0.1rem',
                      }}
                    >
                      {segment.text}
                    </mark>
                  ),
                )
              ) : (
                <span style={{ opacity: 0.6 }}>Matches will appear here.</span>
              )}
            </div>
          </div>

          {result.ok && result.matches.length > 0 ? (
            <div className="tool-live-field tool-live-field-full">
              <span>Match details</span>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                {result.matches.slice(0, 200).map((match, index) => (
                  <div
                    key={index}
                    style={{
                      border: '1px solid rgba(148, 163, 184, 0.35)',
                      borderRadius: '0.5rem',
                      padding: '0.6rem 0.75rem',
                      fontSize: '0.85rem',
                    }}
                  >
                    <div style={{ fontFamily: 'var(--font-mono, monospace)' }}>
                      <strong>#{index + 1}</strong> [{match.index}–{match.end}]{' '}
                      <span style={{ opacity: 0.85 }}>
                        {match.value === '' ? '(empty match)' : match.value}
                      </span>
                    </div>
                    {match.groups.length > 0 ? (
                      <div style={{ marginTop: '0.35rem', opacity: 0.85 }}>
                        {match.groups.map((group, groupIndex) => (
                          <div
                            key={groupIndex}
                            style={{ fontFamily: 'var(--font-mono, monospace)' }}
                          >
                            group {groupIndex + 1}: {group === undefined ? '(undefined)' : group}
                          </div>
                        ))}
                      </div>
                    ) : null}
                    {match.namedGroups
                      ? Object.entries(match.namedGroups).map(([name, value]) => (
                          <div
                            key={name}
                            style={{
                              marginTop: '0.2rem',
                              opacity: 0.85,
                              fontFamily: 'var(--font-mono, monospace)',
                            }}
                          >
                            {name}: {value === undefined ? '(undefined)' : value}
                          </div>
                        ))
                      : null}
                  </div>
                ))}
              </div>
            </div>
          ) : null}
          <p className="tool-live-panel-note">Evaluated locally in your browser.</p>
        </section>
      </div>
    </div>
  );
}
